// src/components/TileBuilder/ChartControls/PieDonutGaugeSpecificControls.tsx
import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { setSettings } from '../../../../support/slices/tileBuilderSlice';
import { ChartControlProps } from './ChartControlProps';

const CENTER_MODES = [
  { value: 'none', label: 'None' },
  { value: 'total', label: 'Total (auto)' },
  { value: 'custom', label: 'Custom HTML' },
  { value: 'slot', label: 'Slot (centerSlotName)' },
] as const;

const CENTER_VALUE_FORMATS = [
  { value: 'suffix', label: 'Suffix (e.g. 1.2K)' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'raw', label: 'Raw (no formatting)' },
] as const;

const PieDonutGaugeSpecificControls: React.FC<ChartControlProps> = ({ chartId, settings, dispatch, chartKind }) => {
  const s: any = settings as any;

  const isDonut = chartKind === 'donut';
  const isGauge = chartKind === 'gauge';
  const isPie = chartKind === 'pie';


  const centerMode = (s.centerMode ?? 'total' ) as (typeof CENTER_MODES)[number]['value'];

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
      <Typography variant="subtitle2" sx={{ width: '100%' }}>
        {isPie ? 'Pie' : isDonut ? 'Donut' : 'Gauge'} Settings
      </Typography>

      {/* Tooltip behavior: legend hover tooltip should be enabled for all variants by default */}
      <FormControlLabel
        control={
          <Switch
            checked={s.enableLegendTooltip !== false}
            onChange={(e) =>
              dispatch(setSettings({ chartId, settings: { enableLegendTooltip: e.target.checked } as any }))
            }
          />
        }
        label="Legend hover tooltip"
      />

      {/* Interactivity / accessibility */}
      <FormControlLabel
        control={
          <Switch
            checked={s.isInteractive !== false}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { isInteractive: e.target.checked } as any }))}
          />
        }
        label="Interactive"
      />

      <FormControlLabel
        control={
          <Switch
            checked={!!s.isFocusable}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { isFocusable: e.target.checked } as any }))}
          />
        }
        label="Focusable (a11y)"
      />

      {(isDonut || isGauge) && (
        <TextField
          label="Inner Radius"
          type="number"
          size="small"
          value={typeof s.innerRadius === 'number' ? s.innerRadius : 0.7}
          onChange={(e) => dispatch(setSettings({ chartId, settings: { innerRadius: Number(e.target.value) } as any }))}
          sx={{ width: 180 }}
          inputProps={{ step: 0.01, min: 0, max: 1 }}
          helperText="0..1"
        />
      )}

      {isGauge && (
        <>
          <TextField
            label="Start Angle"
            type="number"
            size="small"
            value={typeof s.startAngle === 'number' ? s.startAngle : -90}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { startAngle: Number(e.target.value) } as any }))}
            sx={{ width: 180 }}
            inputProps={{ min: -360, max: 360 }}
          />
          <TextField
            label="End Angle"
            type="number"
            size="small"
            value={typeof s.endAngle === 'number' ? s.endAngle : 90}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { endAngle: Number(e.target.value) } as any }))}
            sx={{ width: 180 }}
            inputProps={{ min: -360, max: 360 }}
          />
        </>
      )}

      {/* Arc label toggles */}
      <FormControlLabel
        control={
          <Switch
            checked={!!s.enableArcLabels}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { enableArcLabels: e.target.checked } as any }))}
          />
        }
        label="Arc labels"
      />

      <FormControlLabel
        control={
          <Switch
            checked={s.enableArcLinkLabels !== false}
            onChange={(e) =>
              dispatch(setSettings({ chartId, settings: { enableArcLinkLabels: e.target.checked } as any }))
            }
          />
        }
        label="Arc link labels"
      />

      {/* Center content controls */}
      <Typography variant="subtitle2" sx={{ width: '100%', mt: 1 }}>
        Center Content
      </Typography>

      <FormControl sx={{ minWidth: 240 }} size="small">
        <InputLabel>Center Mode</InputLabel>
        <Select
          label="Center Mode"
          value={centerMode}
          onChange={(e) => dispatch(setSettings({ chartId, settings: { centerMode: e.target.value as any } as any }))}
        >
          {CENTER_MODES.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {centerMode === 'total' && (
        <>
          <FormControl sx={{ minWidth: 240 }} size="small">
            <InputLabel>Center Value Format</InputLabel>
            <Select
              label="Center Value Format"
              value={s.centerValueFormat ?? 'suffix'}
              onChange={(e) =>
                dispatch(setSettings({ chartId, settings: { centerValueFormat: e.target.value as any } as any }))
              }
            >
              {CENTER_VALUE_FORMATS.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {s.centerValueFormat === 'currency' && (
            <TextField
              label="Currency"
              size="small"
              value={s.currency ?? 'USD'}
              onChange={(e) => dispatch(setSettings({ chartId, settings: { currency: e.target.value } as any }))}
              sx={{ width: 140 }}
            />
          )}

          <TextField
            label="Center Title"
            size="small"
            value={s.centerTitle ?? ''}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { centerTitle: e.target.value } as any }))}
            sx={{ minWidth: 220 }}
            placeholder="e.g. Total"
          />

          <TextField
            label="Center Subtitle"
            size="small"
            value={s.centerSubtitle ?? ''}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { centerSubtitle: e.target.value } as any }))}
            sx={{ minWidth: 220 }}
            placeholder="e.g. Clients"
          />
        </>
      )}

      {centerMode === 'custom' && (
        <TextField
          label="Center HTML"
          size="small"
          value={s.centerHtml ?? ''}
          onChange={(e) => dispatch(setSettings({ chartId, settings: { centerHtml: e.target.value } as any }))}
          sx={{ minWidth: 520, flex: '1 1 520px' }}
          multiline
          minRows={3}
          placeholder={`<div style="display:flex;height:100%;align-items:center;justify-content:center;">Hello</div>`}
          helperText="HTML string injected into centerContent."
        />
      )}

      {centerMode === 'slot' && (
        <TextField
          label="Center Slot Name"
          size="small"
          value={s.centerSlotName ?? ''}
          onChange={(e) => dispatch(setSettings({ chartId, settings: { centerSlotName: e.target.value } as any }))}
          sx={{ minWidth: 260 }}
          placeholder="e.g. center"
          helperText="When set, centerContent is ignored and a center slot is used."
        />
      )}

      {/* State-style coloring support (optional) */}
      <FormControlLabel
        control={
          <Switch
            checked={!!s.useDatumColorField}
            onChange={(e) =>
              dispatch(setSettings({ chartId, settings: { useDatumColorField: e.target.checked } as any }))
            }
          />
        }
        label="Use datum.color field"
      />
    </Box>
  );
};

export default PieDonutGaugeSpecificControls;